import { useState, useCallback, useMemo, useRef, useEffect, useContext } from 'react';
import { CellClickedEvent } from 'ag-grid-community';
import { MyContext } from '../../../context/myContext';
import workItemStateCategory from '../../../api/WorkItemStateCategory';
import useApi from '../../../hooks/useApi';
import { generateRandomId } from '../../config/UserConfig';
import { getStateCategoryDefs } from './WorkItemStateCategoryDefs';
import TsGrid from '../../../components/tsGrid';
import { toast } from 'react-toastify';

export type StateCategory = {
    id: number,
    name: string,
    work_in_progress_state: boolean
};

const StateCategoriesTable = ({ getData, data }: any) => {
    const { selectedProduct } = useContext<any>(MyContext);
    const saveStateCategoryApi = useApi(workItemStateCategory.saveStateCategory)
    const [uniqueCategories, setUniqueCategories] = useState([])
    const [workItemStates, setWorkItemStates] = useState([])
    const gridApiRef = useRef<any>(null);
    const [rowData, setRowData] = useState<StateCategory[]>([]);


    const fetchData = async () => {
        const response = data

        setRowData([...response?.data?.data?.workItemStateCategories, { id: generateRandomId() }])
        setUniqueCategories(response?.data?.data?.workItemStateCategories?.map((item: StateCategory) => { return item?.name }))
        setWorkItemStates(response?.data?.data?.workItemStates)

    }

    useEffect(() => {
        if (data?.data?.data)
            fetchData()
    }, [data])

    const saveStateCategory = async (data: StateCategory) => {
        try {
            const obj = {
                ...data,
                id: typeof data?.id === 'number' ? data?.id : 0,
                name: data.name.trim(),
                work_in_progress_state: data?.work_in_progress_state ?? false,
            }
            const response = await saveStateCategoryApi.request(selectedProduct, obj)
            if (response?.data) {
                getData()
            }
        }
        catch (err) {

        }
    }


    const scrollToBottom = useCallback(() => {
        if (gridApiRef.current) {
            const rowCount = gridApiRef.current.getDisplayedRowCount();
            gridApiRef.current.ensureIndexVisible(rowCount - 1, "bottom");
        }
    }, []);

    const onGridReady = useCallback((params: any) => {
        gridApiRef.current = params.api;
        params.api.sizeColumnsToFit();
    }, []);

    const addNewRow = useCallback(() => {
        const newItem = { id: generateRandomId() };
        if (gridApiRef.current) {
            gridApiRef.current.applyTransaction({ add: [newItem] });
            setTimeout(() => {
                scrollToBottom();
            }, 10);
        }
    }, [rowData, scrollToBottom]);

    const onCellValueChanged = (params: any) => {
        const rowCount = gridApiRef.current.getDisplayedRowCount();
        const rowNode = gridApiRef.current.getRowNode(params.node.rowIndex);
        const lastRowIndex = rowCount - 1;
        const data = rowNode.data;

        if (params?.colDef?.field === 'name') {
            data['name'] = params?.data?.name;
            const isAlreadyPresent = uniqueCategories.find((item: any) => (item?.trim().toLowerCase() === params?.newValue?.trim().toLowerCase()));
            if (isAlreadyPresent) {
                data['name'] = params?.oldValue;
                gridApiRef.current.applyTransaction({ update: [data] });
                toast.error('category name already present', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
                return
            }
        }

        if (params.rowIndex === lastRowIndex && data['name']) {
            addNewRow();
            gridApiRef.current.ensureIndexVisible(rowCount - 1, "bottom");
        }
        saveStateCategory(data)
    };

    const onCellClicked = (params: CellClickedEvent) => {
        const rowIndex = params.rowIndex || 0;
        const columnKey = params?.column?.getColId();
        const rowNode = gridApiRef.current.getRowNode(rowIndex);
        const data = rowNode.data;

        if ((columnKey === "work_in_progress_state")) {
            if (rowNode) {
                data[columnKey] = !data[columnKey];
                gridApiRef.current.applyTransaction({ update: [data] });
                if (data?.name) {
                    saveStateCategory(data);
                }
            }
        }

    }

    const afterUpdate = () => {
        getData()
    }

    const columnDefs = useMemo(() => getStateCategoryDefs({
        afterUpdate,
        selectedProduct,
        workItemStates
    }), [addNewRow, rowData, selectedProduct, workItemStates]);

    const defaultColDef = {
        flex: 1,
        resizable: true,
    };


    return (
        <div>
            <h1 className="page-heading">Work Item State Categories</h1>
            <div style={{ height: '85vh' }} className={`ag-theme-alpine grid_style mt-[30px] pr-[10px]`}>
                <TsGrid
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    singleClickEdit={true}
                    stopEditingWhenCellsLoseFocus={true}
                    onCellValueChanged={onCellValueChanged}
                    onGridReady={onGridReady}
                    onCellClicked={onCellClicked}
                    suppressScrollOnNewData={true}
                    editType={"fullRowInline"}
                    virtualization={true}

                />
            </div>
        </div>
    );
};

export default StateCategoriesTable;